<template>
	<div class="container">
		<div class="grid-md">
			<div class="col-8-md col-offset-2-md position-relative">
				<h1 class="heading-1 text-align-center">Full disclosure</h1>
				<section class="bt mt-300 pt-300">
					<h2 class="heading-5">Table of contents</h2>
					<ol class="ol">
						<li v-for="anchor in inPageNavInfo.anchors" :key="anchor.slug">
							<BaseLink
								:link="{
									router: inPageNavInfo.routeName,
									hash: '#' + anchor.slug,
									text: anchor.heading
								}"
							/>
						</li>
					</ol>
				</section>
				<section :id="inPageNavInfo.anchors[0].slug" class="bt mt-300 pt-300">
					<h2 class="heading-5">1. {{ inPageNavInfo.anchors[0].heading }}</h2>
					<p
						>Fundrise, LLC (&ldquo;Fundrise&rdquo;) operates an online investment platform located at
						<BaseLink :link="{ router: 'home' }">fundrise.com</BaseLink> and through various mobile
						applications (the &ldquo;Platform&rdquo;). The information contained on the Platform neither
						constitutes an offer for nor a solicitation of interest in any securities offering; however, if
						an indication of interest is provided, it may be withdrawn or revoked, without obligation or
						commitment of any kind prior to being accepted following the qualification or effectiveness of
						the applicable offering document, and any offer, solicitation or sale of any securities will be
						made only by means of an offering circular, private placement memorandum, or prospectus. No
						money or other consideration is hereby being solicited, and will not be accepted without such
						potential investor having been provided the applicable offering document. Joining the Platform
						neither constitutes an indication of interest in any offering nor involves any obligation or
						commitment of any kind.</p
					>
					<p
						>Fundrise Advisors, LLC (&ldquo;Fundrise Advisors&rdquo;), an affiliate of Fundrise, is
						registered with the U.S. Securities and Exchange Commission (&ldquo;SEC&rdquo;) as an investment
						adviser under the Investment Advisers Act of 1940. Financial advisory services are only provided
						to investors who become Fundrise Advisors Clients (&ldquo;Clients&rdquo;) pursuant to a written
						<BaseLink
							:link="{
								router: 'offering-circulars',
								hash: '#advisors-disclosures-client-agreement',
								text: 'Client Agreement'
							}"
						/>, which investors are urged to read, along with
						<BaseLink
							:link="{
								router: 'offering-circulars',
								hash: '#advisors-disclosures-2'
							}"
							>Part 2 of Fundrise Advisors&rsquo; Form ADV</BaseLink
						>, and carefully consider in determining whether such agreement is suitable for their individual
						facts and circumstances. Registration with the SEC does not imply a certain level of skill or
						training.</p
					>
					<p>
						PAST PERFORMANCE IS NO GUARANTEE OF FUTURE RESULTS, AND ANY EXPECTED RETURNS OR HYPOTHETICAL
						PROJECTIONS MAY NOT REFLECT ACTUAL FUTURE PERFORMANCE. FURTHERMORE, PAST RETURNS MAY REFLECT THE
						PERFORMANCE OF ASSETS FOR A FINITE TIME, OR DURING A PERIOD OF EXTREME MARKET ACTIVITY. ALL
						INVESTMENTS INVOLVE RISK AND MAY RESULT IN PARTIAL OR TOTAL LOSS. There can be no assurance that
						an investment mix or any projected or actual performance shown on the Platform will lead to the
						expected results shown or perform in any predictable manner. It should not be assumed that
						investors will experience returns in the future, if any, comparable to those shown or that any
						or all investors on the Platform experienced such returns.
					</p>
					<p>
						All product names, logos, and brands are property of their respective owners. Use of these
						names, logos, and brands is for identification purposes only, and does not imply endorsement or
						affiliation.
					</p>
					<p>Fundrise Lending, LLC operates in California under CFL license #60BDO 42088.</p>
					<p>
						For investors and potential investors who are residents of the State of Washington, please send
						all correspondence, including any questions or comments, to
						<BaseLink :link="{ href: 'mailto:washingtonstate@fundrise.com' }"
							>washingtonstate@fundrise.com</BaseLink
						>.
					</p>
				</section>
				<section :id="inPageNavInfo.anchors[1].slug" class="bt mt-300 pt-300">
					<h2 class="heading-5">2. {{ inPageNavInfo.anchors[1].heading }}</h2>
					<p>
						Performance information is presented net of all management and advisory fees and expenses unless
						marked otherwise. Commissions are not considered since investors on the Platform are not charged
						trading commissions or any other transaction-based fees. For all periods the performance
						information includes the reinvestment of dividends and interest unless otherwise noted.
					</p>
					<p
						>Fundrise performance figures consist of the aggregate returns of all Client accounts during any
						periods indicated, calculated using the Modified Dietz method, inclusive of dividends and
						capital gains / losses, are net of fees, and include shares which were acquired via dividend
						reinvestment. Fundrise Client returns do not include investments directly in Rise Companies
						Corp. itself, the Fundrise Opportunity Fund, Fundrise Innovation Fund, or Fundrise Opportunistic
						Credit Fund.</p
					>
					<p
						>All relevant information about the assets held by each fund may be found in their respective
						offering circulars or prospectuses, all of which are available at
						<BaseLink :link="offeringCircularsLink">fundrise.com/oc</BaseLink>.</p
					>
					<p
						>Each investment plan is made up solely of certain funds (each such fund, a &ldquo;Designated
						Issuer&rdquo;) that the Client has agreed to have included in such Client&rsquo;s chosen
						investment plan. Each investment plan and their corresponding Designated Issuers are publicly
						available at <BaseLink :link="offeringCircularsLink">fundrise.com/oc</BaseLink>.
					</p>
					<p>
						ANY PROJECTED AND/OR HYPOTHETICAL PERFORMANCE (INCLUDING PERFORMANCE REFLECTED THROUGH ANY
						INTERACTIVE TOOL ON THE PLATFORM) IS PRESENTED FOR ILLUSTRATIVE PURPOSES ONLY AND DOES NOT
						REPRESENT AN ACTUAL INVESTMENT IN ANY OF THE FUNDS SPONSORED BY RISE COMPANIES CORP. (OFFERING
						CIRCULARS AND PROSPECTUSES AVAILABLE AT
						<BaseLink :link="offeringCircularsLink">FUNDRISE.COM/OC</BaseLink>), MAY NOT REFLECT THE
						POTENTIAL EFFECT OF MATERIAL ECONOMIC AND MARKET RELATED FACTORS, AND DO NOT REPRESENT THE
						ACTUAL PERFORMANCE OR EXPERIENCE OF ANY GIVEN INVESTOR ON THE PLATFORM. THERE IS NO GUARANTEE
						THAT ANY PROJECTED AND/OR HYPOTHETICAL PERFORMANCE WILL BE ACHIEVED OR THAT ACTIONS BASED ON
						SUCH PROJECTIONS WILL ACHIEVE AN INTENDED OUTCOME. INVESTORS SHOULD CAREFULLY REVIEW THE
						ADDITIONAL INFORMATION PRESENTED ON THE PLATFORM IN CONNECTION WITH ANY PROJECTIONS. SUCH
						PERFORMANCE FIGURES ARE INCLUSIVE OF APPRECIATION AND DIVIDEND REINVESTMENT, AND NET OF FEES.
					</p>
					<p
						>The funds managed by Fundrise Advisors generally pay quarterly distributions that may consist
						of ordinary dividends, capital gains, and/or nondividend distributions. However, the tax
						characteristics of these distributions (i.e., dividend income, nondividend distributions, and
						capital gains) cannot be determined until after the end of the year. Since Fundrise Advisors
						cannot know the taxability of each fund's distributions during the year, unadjusted effective
						yields are calculated and presented. For certain funds (i.e., those registered under the
						Investment Company Act of 1940), such tax characteristics of these distributions are estimated
						on a quarterly basis and disclosed to shareholders at that time. After the end of each year,
						each entity required to report changes in tax basis shall publicly post a Form 8937 disclosing
						the nondividend distribution portion of such distributions, available at
						<BaseLink :link="offeringCircularsLink">fundrise.com/oc</BaseLink>.</p
					>
					<p
						>The return, composite and performance information shown on the Platform uses or includes
						information compiled from third-party sources, including independent market quotations and index
						information. While we believe the third-party information comes from reliable sources, we do not
						guarantee the accuracy of the Platform information and may receive incorrect information from
						third-party providers. Unless otherwise indicated, the information on the Platform has been
						prepared by us and has not been reviewed, compiled or audited by any independent third-party or
						public accountant.</p
					>
					<p
						>ANY COMPARISONS TO INDICES OR EXCHANGE TRADED FUNDS (“ETFS”) ARE PROVIDED FOR ILLUSTRATIVE
						PURPOSES ONLY. INDICES AND ETFS ARE BROADLY DIVERSIFIED, UNMANAGED GROUP OF SECURITIES, WHICH
						MAY INCLUDE ONLY LARGE CAPITALIZATION COMPANIES OR COMPANIES OF A CERTAIN SIZE. BROADLY BASED
						INDICES AND ETFS ARE ONLY SHOWN AS AN INDICATION OF THE GENERAL PERFORMANCE OF THE FINANCIAL
						MARKETS DURING THE PERIODS INDICATED. BECAUSE OF THE DIFFERENCES BETWEEN THE INVESTMENTS
						AVAILABLE ON THE PLATFORM AND ANY INDICES OR ETFS SHOWN, INVESTORS ARE CAUTIONED THAT NO INDEX
						OR ETF IS DIRECTLY COMPARABLE TO THE PERFORMANCE SHOWN SINCE EACH INDEX OR ETF HAS ITS OWN
						UNIQUE RESULTS AND VOLATILITY, AND SUCH INDICES OR ETFS, IF SHOWN, SHOULD NOT BE RELIED UPON AS
						AN ACCURATE COMPARISON.</p
					>
				</section>
				<section :id="inPageNavInfo.anchors[2].slug" class="bt mt-300 pt-300">
					<h2 class="heading-5">3. {{ inPageNavInfo.anchors[2].heading }}</h2>
					<p>
						General, non-personalized recommendations, services and fees by Fundrise Advisors may vary for
						each Client. Advisory fees, if any, are calculated based upon the amount of assets being managed
						(as detailed further in
						<BaseLink
							:link="{
								router: 'offering-circulars',
								hash: '#advisors-disclosures-2',
								text: 'Fundrise Advisors Form ADV Part 2'
							}"
						/>
						and
						<BaseLink
							:link="{
								router: 'offering-circulars',
								hash: '#advisors-disclosures-crs',
								text: 'Fundrise Advisors Form CRS'
							}"
						/>
						).
					</p>
					<p>
						Fee waivers only apply to fees charged by Fundrise Advisors for investment advisory services and
						do not include any fees or expenses charged by Fundrise Advisors or its affiliates to the
						individual funds available on the Platform.
					</p>
					<p>
						Not all invitations sent through the Fundrise Invitation Program will be considered valid.
						Fundrise Advisors reserves the right, in its sole discretion, to determine that any reward
						offered through the Fundrise Invitation Program (including more favorable fee arrangements,
						reduced or waived advisory fees, and/or vouchers that can be redeemed for shares of certain
						Fundrise-sponsored funds) is not warranted, and/or to change the terms of such rewards with or
						without notice. Any such rewards are not valid with any other offers and are non-transferrable.
						Offer available to U.S. residents only. The total value of any reward(s) offered through the
						Fundrise Invitation Program shall not exceed $1,000 in any given 12-month period per individual
						investor. Fundrise Advisors reserves the right to terminate any such reward at any time for any
						reason, and to refuse to honor any such reward if Fundrise Advisors determines that it was
						obtained under wrongful or fraudulent circumstances, that inaccurate or incomplete information
						was provided in opening the account or connecting the bank account, that any rules or
						regulations would be violated, or that any terms of the Fundrise Client Agreements have been
						violated.
					</p>
				</section>
				<section :id="inPageNavInfo.anchors[3].slug" class="bt mt-300 pt-300">
					<h2 class="heading-5">4. {{ inPageNavInfo.anchors[3].heading }}</h2>
					<p
						>The publicly filed offering circulars and prospectuses of the issuers sponsored by Rise
						Companies Corp., not all of which may be currently qualified by the Securities and Exchange
						Commission, may be found at
						<BaseLink :link="offeringCircularsLink">fundrise.com/oc</BaseLink>.</p
					>
					<p>
						Some of the statements contained on the Platform are forward-looking statements. You should not
						rely upon forward-looking statements as predictions of future events. These statements involve
						known and unknown risks, uncertainties, and other factors that may cause actual results, levels
						of activity, performance, or achievements to be materially and adversely different from any
						future results, levels of activity, performance, or achievements expressed or implied by these
						forward-looking statements. An investment in any issuer available on the Platform involves
						substantial risks, including risk of partial or total loss of investment, and each investor
						should carefully consider the Risk Factors contained in each fund's offering circular or
						prospectus available at
						<BaseLink :link="offeringCircularsLink">fundrise.com/oc</BaseLink>. Except as required by law,
						neither we nor any other person assumes responsibility for the accuracy and completeness of the
						forward-looking statements. We undertake no obligation to update publicly any forward-looking
						statements for any reason to conform these statements to actual results or to changes in our
						expectations.
					</p>
					<p>Diversification does not guarantee profit or protect against loss.</p>
					<p>
						The information presented regarding each issuer's offering, including information regarding
						financial status, business strategy, investment objectives, historical performance, projections,
						redemption plans, etc., merely constitute a summary of such information and is qualified in its
						entirety by the disclosure contained in each issuer's offering circular or prospectus available
						at <BaseLink :link="offeringCircularsLink">fundrise.com/oc</BaseLink>. There can be no guarantee
						that any issuer available on the Platform will achieve its business objectives or that it will
						become profitable.
					</p>
					<p
						>Offers to sell, or solicitations of offers to buy, any security can only be made through
						official offering documents that contain important information about risks, fees and expenses.
						Investors should conduct their own due diligence, not rely on the financial assumptions or
						estimates displayed on the Platform, and are encouraged to consult with a financial advisor,
						attorney, accountant, and any other professional that can help you to understand and assess the
						risks associated with any investment opportunity.</p
					>
					<p
						>The offerings available on the Platform are being conducted as a continuous offerings pursuant
						to either Rule 251(d)(3) or Rule 415 of the Securities Act, meaning that while an offering of
						securities is continuous, sales and settlement of securities may happen sporadically over the
						term of such offering as we are able to process subscriptions. Further, the acceptance of
						subscriptions, whether via the Platform or otherwise, may be briefly paused at times to allow us
						to effectively and accurately process and settle subscriptions that have been received.</p
					>
					<p
						>Neither the SEC nor any state securities commission or regulatory authority approved, passed
						upon or endorsed the merits of any investment on the Platform.</p
					>
				</section>
				<section :id="inPageNavInfo.anchors[4].slug" class="bt mt-300 pt-300">
					<h2 class="heading-5">5. {{ inPageNavInfo.anchors[4].heading }}</h2>
					<p
						>Past performance is not indicative of future results. The information presented regarding Rise
						Companies Corp.'s historical performance is not indicative of future results, and is qualified
						in its entirety by disclosure contained in the Business and Management's Discussion and
						Analysis, as well as the other sections, of the Rise Companies Corp. Offering Circular,
						available at <BaseLink :link="offeringCircularsLink">fundrise.com/oc</BaseLink>. Any historical
						performance of Rise Companies Corp. may not reflect actual future performance and any investor
						in Rise Companies Corp. may experience different results from those shown. AN INVESTMENT IN THE
						CLASS B COMMON STOCK OF RISE COMPANIES CORP. INVOLVES SUBSTANTIAL RISKS, INCLUDING THE POTENTIAL
						OF TOTAL OR PARTIAL LOSS OF INVESTMENT TO INVESTORS. EACH INVESTOR SHOULD CAREFULLY CONSIDER THE
						RISK FACTORS IN ADDITION TO THE OTHER INFORMATION CONTAINED IN THE RISE COMPANIES CORP. OFFERING
						CIRCULAR BEFORE PURCHASING SHARES. The price per share of the Class B Common Stock of Rise
						Companies Corp. is determined on an arbitrary basis by the Rise Companies Corp. board of
						directors and may be changed from time to time by the board of directors in its sole discretion.
						The risks and uncertainties discussed in the Offering Circular are not the only ones Rise
						Companies Corp. faces, but do represent those risks and uncertainties that we believe are most
						significant to its business, operating results, prospects and financial condition. Some
						statements in the Offering Circular and on this website, including statements in the Risk
						Factors, constitute forward-looking statements. Please refer to the section of the Offering
						Circular entitled &ldquo;Statements Regarding Forward-Looking Information&ldquo;.</p
					>
					<p>
						Unlike certain investment opportunities sponsored by Rise Companies Corp., such as the Fundrise
						sponsored funds, Rise Companies Corp. has not historically paid any dividends and has no present
						intention to begin paying dividends in the future. Any statements on www.fundrise.com discussing
						&ldquo;Client returns&ldquo;, or similar terms, relate solely to investments in funds sponsored
						by Rise Companies Corp. and are exclusive of investments in Rise Companies Corp. itself.
						Prospective investors should not expect that returns, if any, from an investment in Rise
						Companies Corp. will be as a result of dividends.
					</p>
					<p>
						Disclosure presented regarding Rise Companies Corp.&rsquo;s business strategy is qualified in
						its entirety by the disclosure contained in the &ldquo;Business&ldquo; section of the Rise
						Companies Corp. Offering Circular, which is available at
						<BaseLink :link="offeringCircularsLink">fundrise.com/oc</BaseLink>. There can be no guarantee
						that Rise Companies Corp. will achieve its business objectives or that it will become
						profitable.
					</p>
					<p>
						Any projections on the future business initiatives of Rise Companies Corp. may not prove to be
						accurate, and are highly dependent on the assumption that Rise Companies Corp. continues to
						introduce new products that are successful in the marketplace, and are qualified in its entirety
						by the disclosure contained in the Rise Companies Corp. Offering Circular. However, there can be
						no guarantee that Rise Companies Corp. will achieve any of its business objectives, that it will
						get to scale or that it will become profitable. INVESTING IN RISE COMPANIES CORP. IS AN
						INHERENTLY RISKY INVESTMENT THAT MAY RESULT IN TOTAL OR PARTIAL LOSS OF INVESTMENT TO INVESTORS.
					</p>
					<p>
						Although Rise Companies Corp. intends to complete a transaction (such as a sale or registered
						initial public offering) providing liquidity to shareholders at some point in the future, its
						governing documents do not require it to pursue such a liquidity transaction. Market conditions
						and other factors could cause us to delay the listing of our shares on a national securities
						exchange or delay the commencement of a liquidation or other type of liquidity transaction, such
						as a merger or sale of assets. However, there can be no guarantee that any future liquidity
						event for shareholders will be profitable, and may result in a total or partial loss to
						investors.
					</p>
				</section>
				<section :id="inPageNavInfo.anchors[5].slug" class="bt mt-300 pt-300">
					<h2 class="heading-5">6. {{ inPageNavInfo.anchors[5].heading }}</h2>
					<p
						>Any statements made via Fundrise social media sites are not intended as investment, tax, or
						legal advice. This content is not an offer, solicitation of an offer, or advice to buy or sell
						securities. All securities involve risk and may result in partial or total loss. Past
						performance is not a guarantee of future results. Any historical returns, expected returns, or
						probability projections may not reflect actual future performance. Fundrise reserves the right
						to remove any comment, including those that we deem to be spam, offensive or otherwise
						inappropriate. Investment advisory services are provided by Fundrise Advisors LLC, an
						SEC-registered investment adviser.</p
					>
					<p
						>Fundrise does not approve or endorse any third-party communications posted on its site(s) and
						any such postings should not be attributed to Fundrise. Fundrise will not treat any social media
						post as a formal complaint. To submit a formal complaint regarding Fundrise, please contact
						<BaseLink :link="{ href: 'mailto:investments@fundrise.com' }">investments@fundrise.com</BaseLink
						>.</p
					>
					<p
						>Any third-party links provided in posts by Fundrise social media are offered as a matter of
						convenience and are not intended to imply that Fundrise, its affiliates or employees endorse,
						sponsor, promote, and/or are affiliated with the owners of or participants in those sites, or
						endorse any information contained on those sites, unless expressly stated otherwise.</p
					>
					<p>
						In addition, any third party publications reflect solely the opinions of the authors, do not
						reflect the views of Fundrise, and Fundrise does not independently certify the information
						contained therein. Any such content is meant for informational purposes only and is not intended
						to serve as a recommendation to buy or sell any security, and is not an offer or sale of any
						security, and is not intended to serve as the basis for any investment decision. Past
						performance is not indicative of future results, and all investments may result in total or
						partial loss.
					</p>
					<p
						>Testimonials may not be representative of the experience of other or future clients, and past
						performance, including testimonials, is no guarantee of future performance or success. All
						investing includes risk of loss of principal. Unless otherwise specified (e.g.,
						#paidtestimonial, #paidendorsement, #Fundrisetestimonial, or #Fundriseendorsement), any
						testimonial or endorsement by an unrelated third party has neither been solicited nor paid
						for.</p
					>
					<p
						>The publicly filed offering circulars and prospectuses of the issuers sponsored by Rise
						Companies Corp., not all of which may be currently qualified by the Securities and Exchange
						Commission, may be found at <BaseLink :link="offeringCircularsLink">fundrise.com/oc</BaseLink>.
					</p>
					<p
						>To contact us directly, please visit our website
						<BaseLink :link="{ router: 'home' }">https://fundrise.com</BaseLink> or contact us by mail at 11
						Dupont Circle NW, 9th Floor, Washington, D.C. 20036 or by email:
						<BaseLink :link="{ href: 'mailto:investments@fundrise.com' }">investments@fundrise.com</BaseLink
						>. For investors and potential investors who are residents of the State of Washington, please
						send all correspondence, including any questions or comments, to
						<BaseLink :link="{ href: 'mailto:washingtonstate@fundrise.com' }"
							>washingtonstate@fundrise.com</BaseLink
						>.</p
					>
				</section>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { InPageNavInfo } from 'types/layout';

interface Props {
	routeName?: string;
}

const props = withDefaults(defineProps<Props>(), {
	routeName: 'disclosures'
});

const offeringCircularsLink = { router: 'offering-circulars' };

const inPageNavInfo: InPageNavInfo = {
	routeName: props.routeName,
	anchors: [
		{
			slug: 'general-disclosure',
			heading: 'General disclosure'
		},
		{
			slug: 'performance-disclosure',
			heading: 'Historical and projected performance disclosure'
		},
		{
			slug: 'fundrise-advisors-fee-and-account-disclosures',
			heading: 'Fundrise Advisors fee and account disclosures'
		},
		{
			slug: 'offering-disclosure',
			heading: 'Offering disclosure'
		},
		{
			slug: 'rise-companies-disclosure',
			heading: 'Rise Companies offering (Fundrise iPO) disclosure'
		},
		{
			slug: 'social-media-disclosure',
			heading: 'Social Media Disclosure'
		}
	]
};
</script>
